<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

import { useFocusTrap } from '@/composables/useFocusTrap';
import { settleConfirm, useConfirmState } from '@/services/confirm';

const pending = useConfirmState();
const input = ref('');
const inputEl = ref<HTMLTextAreaElement | null>(null);
const dialogEl = ref<HTMLElement | null>(null);
const cancelEl = ref<HTMLButtonElement | null>(null);
const confirmEl = ref<HTMLButtonElement | null>(null);
const touched = ref(false);
// 打开前焦点所在的元素（通常是触发按钮），关闭后把焦点还回去
let trigger: HTMLElement | null = null;

const needsInput = computed(() => Boolean(pending.value?.prompt));
const missing = computed(() =>
  Boolean(pending.value?.promptRequired) && !input.value.trim());

function cancel(): void {
  settleConfirm(null);
}

function confirm(): void {
  if (!pending.value) return;
  touched.value = true;
  // 必填没填时不关闭——原生 prompt 做不到这一点，只能放行空值再由调用方补救
  if (missing.value) {
    inputEl.value?.focus();
    return;
  }
  settleConfirm(needsInput.value ? input.value.trim() : '');
}

// 首个焦点：有输入框落输入框；危险操作落「取消」，免得顺手一个回车就执行了
function initialFocus(): HTMLElement | null {
  if (needsInput.value) return inputEl.value;
  return pending.value?.danger ? cancelEl.value : confirmEl.value;
}

// Esc 交给焦点陷阱（叠在别的弹窗上时只有最上层响应），这里只管回车确认
function onKey(event: KeyboardEvent): void {
  if (!pending.value || event.key !== 'Enter') return;
  // 焦点在按钮上时让按钮自己响应：Tab 到「取消」再回车不能变成确认
  if (event.target instanceof HTMLButtonElement) return;
  // 多行输入里回车是换行，用 Ctrl/⌘+Enter 提交
  if (!needsInput.value || event.ctrlKey || event.metaKey) {
    event.preventDefault();
    confirm();
  }
}

useFocusTrap(dialogEl, {
  active: computed(() => Boolean(pending.value)),
  onEscape: cancel,
  restoreFocus: () => {
    trigger?.focus();
    trigger = null;
  },
  initialFocus,
});

watch(pending, async (next, prev) => {
  if (!next) return;
  input.value = next.defaultValue ?? '';
  touched.value = false;
  if (!prev) {
    // 从关着到打开：记下触发元素，落焦交给焦点陷阱
    trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return;
  }
  // 上一个框没关就弹下一个：陷阱一直开着不会重新落焦，这里补一次；触发元素沿用第一个
  await nextTick();
  initialFocus()?.focus();
});

onMounted(() => window.addEventListener('keydown', onKey));
onUnmounted(() => window.removeEventListener('keydown', onKey));
</script>

<template>
  <div v-if="pending" class="confirm-mask" @click.self="cancel">
    <section
      ref="dialogEl"
      class="confirm-dialog"
      role="alertdialog"
      aria-modal="true"
      :aria-label="pending.title"
    >
      <h3>{{ pending.title }}</h3>
      <p v-if="pending.message" class="confirm-message">{{ pending.message }}</p>

      <label v-if="needsInput" class="confirm-input">
        <span>{{ pending.prompt }}</span>
        <textarea
          ref="inputEl"
          v-model="input"
          rows="3"
          maxlength="1000"
          :placeholder="pending.promptPlaceholder ?? ''"
        ></textarea>
        <small v-if="touched && missing" class="confirm-required">这一项必须填写</small>
      </label>

      <footer>
        <button ref="cancelEl" class="button button-secondary" type="button" @click="cancel">
          {{ pending.cancelLabel ?? '取消' }}
        </button>
        <button
          ref="confirmEl"
          class="button"
          :class="pending.danger ? 'button-danger' : 'button-primary'"
          type="button"
          @click="confirm"
        >{{ pending.confirmLabel ?? '确认' }}</button>
      </footer>
    </section>
  </div>
</template>

<style scoped>
/* 确认是「停下来做决定」的时刻，居中模态的阻断感是特性不是缺陷 */
.confirm-mask {
  position: fixed;
  inset: 0;
  /* 在所有抽屉之上（健康度明细 120），提示条 200 之上——要人做决定的框不能被任何东西盖住 */
  z-index: 210;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(15, 23, 36, .45);
  animation: confirm-mask-in var(--duration-overlay, 220ms) var(--ease-out, cubic-bezier(0.23, 1, 0.32, 1));
}

.confirm-dialog {
  width: min(440px, 100%);
  display: grid;
  gap: 12px;
  padding: 22px 24px;
  border-radius: var(--radius);
  background: var(--white);
  box-shadow: 0 24px 60px rgba(15, 23, 36, .28);
  /* Soft-Gate P3：遮罩淡入 + 卡片 scale(0.95)，≤220ms，禁从 0 起缩放 */
  animation: confirm-in var(--duration-overlay, 220ms) var(--ease-out, cubic-bezier(0.23, 1, 0.32, 1));
}

@keyframes confirm-mask-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes confirm-in {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: none; opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .confirm-mask,
  .confirm-dialog {
    animation: none;
  }
}

.confirm-dialog h3 { margin: 0; font-size: 17px; }
.confirm-message { margin: 0; color: var(--muted); font-size: 13px; line-height: 1.8; white-space: pre-wrap; }
.confirm-input { display: grid; gap: 5px; }
.confirm-input span { color: var(--ink); font-size: 12px; font-weight: 700; }
.confirm-input textarea { padding: 8px 10px; border: 1px solid var(--line); background: var(--white); font: inherit; font-size: 13px; resize: vertical; }
.confirm-required { color: var(--risk); font-size: 12px; }
.confirm-dialog footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 2px; }
</style>
